/**
 * hooks/usePlaybookBuilder.ts
 * Playbook management hook for onboarding
 *
 * Manages playbook creation, editing, and deletion with draft persistence.
 * Follows the useFeeHierarchy pattern: local state + sync to draft.
 */

import { useState, useCallback, useEffect } from 'react';
import { useOnboardingData } from '../../../hooks/useOnboardingData';
import { generateUUID } from '@/utils/uuid';

/**
 * Confluence link draft structure (polarity assigned at playbook level)
 */
export interface PlaybookConfluenceLinkDraft {
  confluenceId: string;
  polarity: 'positive' | 'negative';
}

/**
 * Playbook draft structure
 */
export interface PlaybookDraft {
  tempId: string;
  name: string;
  description?: string;
  philosophy?: string;
  criteriaIds?: string[];
  confluences?: PlaybookConfluenceLinkDraft[];
  entryModelIds: string[];
}

/**
 * Market condition category draft structure
 */
export interface MarketConditionCategoryDraft {
  tempId: string;
  name: string;
  description?: string;
  color: string;
  order: number;
}

/**
 * Criteria item draft structure
 */
export interface CriteriaItemDraft {
  tempId: string;
  name: string;
  description?: string;
  categoryId?: string;
}

/**
 * Confluence draft structure (neutral - polarity assigned at playbook level)
 */
export interface ConfluenceDraft {
  tempId: string;
  name: string;
  description?: string;
  categoryId?: string;
}

/**
 * Entry model draft structure
 */
export interface EntryModelDraft {
  tempId: string;
  name: string;
  description?: string;
  categoryId?: string;
}

/**
 * Entry model category draft structure
 */
export interface EntryModelCategoryDraft {
  tempId: string;
  name: string;
  description?: string;
  color: string;
  order: number;
}

/**
 * Operations supported by the playbook builder
 */
export interface PlaybookBuilderOperations {
  // Playbook operations
  /** Add a new playbook */
  addPlaybook: (name: string, description?: string, philosophy?: string) => void;
  /** Update an existing playbook */
  updatePlaybook: (tempId: string, updates: Partial<Omit<PlaybookDraft, 'tempId'>>) => void;
  /** Remove a playbook */
  removePlaybook: (tempId: string) => void;
  /** Get a specific playbook by tempId */
  getPlaybook: (tempId: string) => PlaybookDraft | undefined;

  // Market condition category operations
  /** Add a new market condition category */
  addMarketConditionCategory: (name: string, color: string, description?: string) => string;
  /** Update an existing market condition category */
  updateMarketConditionCategory: (tempId: string, updates: Partial<Omit<MarketConditionCategoryDraft, 'tempId'>>) => void;
  /** Remove a market condition category */
  removeMarketConditionCategory: (tempId: string) => void;
  /** Get a specific market condition category by tempId */
  getMarketConditionCategory: (tempId: string) => MarketConditionCategoryDraft | undefined;

  // Criteria item operations
  /** Add a new criteria item */
  addCriteriaItem: (name: string, description?: string, categoryId?: string) => string;
  /** Update an existing criteria item */
  updateCriteriaItem: (tempId: string, updates: Partial<Omit<CriteriaItemDraft, 'tempId'>>) => void;
  /** Remove a criteria item */
  removeCriteriaItem: (tempId: string) => void;
  /** Get a specific criteria item by tempId */
  getCriteriaItem: (tempId: string) => CriteriaItemDraft | undefined;

  // Confluence operations
  /** Add a new confluence */
  addConfluence: (name: string, description?: string, categoryId?: string) => string;
  /** Update an existing confluence */
  updateConfluence: (tempId: string, updates: Partial<Omit<ConfluenceDraft, 'tempId'>>) => void;
  /** Remove a confluence */
  removeConfluence: (tempId: string) => void;
  /** Get a specific confluence by tempId */
  getConfluence: (tempId: string) => ConfluenceDraft | undefined;

  // Entry model operations
  /** Add a new entry model */
  addEntryModel: (name: string, description?: string, categoryId?: string) => string;
  /** Update an existing entry model */
  updateEntryModel: (tempId: string, updates: Partial<Omit<EntryModelDraft, 'tempId'>>) => void;
  /** Remove an entry model */
  removeEntryModel: (tempId: string) => void;
  /** Get a specific entry model by tempId */
  getEntryModel: (tempId: string) => EntryModelDraft | undefined;

  // Entry model category operations
  /** Add a new entry model category */
  addEntryModelCategory: (name: string, color: string, description?: string) => string;
  /** Update an existing entry model category */
  updateEntryModelCategory: (tempId: string, updates: Partial<Omit<EntryModelCategoryDraft, 'tempId'>>) => void;
  /** Remove an entry model category */
  removeEntryModelCategory: (tempId: string) => void;
  /** Get a specific entry model category by tempId */
  getEntryModelCategory: (tempId: string) => EntryModelCategoryDraft | undefined;
}

/**
 * Playbook builder hook return type
 */
export interface PlaybookBuilderState {
  playbooks: PlaybookDraft[];
  criteriaItems: CriteriaItemDraft[];
  marketConditionCategories: MarketConditionCategoryDraft[];
  confluences: ConfluenceDraft[];
  entryModels: EntryModelDraft[];
  entryModelCategories: EntryModelCategoryDraft[];
}

/**
 * Playbook builder hook
 * Returns [state, operations] tuple
 */
export function usePlaybookBuilder(): [PlaybookBuilderState, PlaybookBuilderOperations] {
  const { draft, updateTrading } = useOnboardingData();

  // Local state (for UI responsiveness)
  const [playbooks, setPlaybooks] = useState<PlaybookDraft[]>([]);
  const [criteriaItems, setCriteriaItems] = useState<CriteriaItemDraft[]>([]);
  const [marketConditionCategories, setMarketConditionCategories] = useState<MarketConditionCategoryDraft[]>([]);
  const [confluences, setConfluences] = useState<ConfluenceDraft[]>([]);
  const [entryModels, setEntryModels] = useState<EntryModelDraft[]>([]);
  const [entryModelCategories, setEntryModelCategories] = useState<EntryModelCategoryDraft[]>([]);

  // Initialize from draft
  useEffect(() => {
    if (draft?.trading?.playbooks) {
      setPlaybooks(draft.trading.playbooks);
    }
    if (draft?.trading?.criteriaItems) {
      setCriteriaItems(draft.trading.criteriaItems);
    }
    if (draft?.trading?.marketConditionCategories) {
      setMarketConditionCategories(draft.trading.marketConditionCategories);
    }
    if (draft?.trading?.confluences) {
      setConfluences(draft.trading.confluences);
    }
    if (draft?.trading?.entryModels) {
      setEntryModels(draft.trading.entryModels);
    }
    if (draft?.trading?.entryModelCategories) {
      setEntryModelCategories(draft.trading.entryModelCategories);
    }
  }, [draft]);

  // Sync local state to draft
  const syncToDraft = useCallback((
    updatedPlaybooks: PlaybookDraft[],
    updatedCriteriaItems?: CriteriaItemDraft[],
    updatedMarketConditionCategories?: MarketConditionCategoryDraft[],
    updatedConfluences?: ConfluenceDraft[],
    updatedEntryModels?: EntryModelDraft[],
    updatedEntryModelCategories?: EntryModelCategoryDraft[]
  ): void => {
    updateTrading({
      playbooks: updatedPlaybooks,
      ...(updatedCriteriaItems !== undefined && { criteriaItems: updatedCriteriaItems }),
      ...(updatedMarketConditionCategories !== undefined && { marketConditionCategories: updatedMarketConditionCategories }),
      ...(updatedConfluences !== undefined && { confluences: updatedConfluences }),
      ...(updatedEntryModels !== undefined && { entryModels: updatedEntryModels }),
      ...(updatedEntryModelCategories !== undefined && { entryModelCategories: updatedEntryModelCategories })
    });
  }, [updateTrading]);

  /**
   * Add a new playbook
   */
  const addPlaybook = useCallback((
    name: string,
    description?: string,
    philosophy?: string
  ): void => {
    const newPlaybook: PlaybookDraft = {
      tempId: generateUUID(),
      name,
      ...(description && { description }),
      ...(philosophy && { philosophy }),
      criteriaIds: [],
      confluences: [],
      entryModelIds: []
    };

    const updatedPlaybooks = [...playbooks, newPlaybook];
    setPlaybooks(updatedPlaybooks);
    syncToDraft(updatedPlaybooks);
  }, [playbooks, syncToDraft]);

  /**
   * Update an existing playbook
   */
  const updatePlaybook = useCallback((
    tempId: string,
    updates: Partial<Omit<PlaybookDraft, 'tempId'>>
  ): void => {
    const updatedPlaybooks = playbooks.map(playbook =>
      playbook.tempId === tempId
        ? { ...playbook, ...updates }
        : playbook
    );

    setPlaybooks(updatedPlaybooks);
    syncToDraft(updatedPlaybooks);
  }, [playbooks, syncToDraft]);

  /**
   * Remove a playbook
   */
  const removePlaybook = useCallback((tempId: string): void => {
    const updatedPlaybooks = playbooks.filter(playbook => playbook.tempId !== tempId);
    setPlaybooks(updatedPlaybooks);
    syncToDraft(updatedPlaybooks);
  }, [playbooks, syncToDraft]);

  /**
   * Get a specific playbook by tempId
   */
  const getPlaybook = useCallback((tempId: string): PlaybookDraft | undefined => {
    return playbooks.find(playbook => playbook.tempId === tempId);
  }, [playbooks]);

  /**
   * Add a new market condition category
   */
  const addMarketConditionCategory = useCallback((
    name: string,
    color: string,
    description?: string
  ): string => {
    const tempId = generateUUID();
    const newCategory: MarketConditionCategoryDraft = {
      tempId,
      name,
      color,
      ...(description && { description }),
      order: marketConditionCategories.length
    };

    const updatedCategories = [...marketConditionCategories, newCategory];
    setMarketConditionCategories(updatedCategories);
    syncToDraft(playbooks, criteriaItems, updatedCategories);
    return tempId;
  }, [marketConditionCategories, criteriaItems, playbooks, syncToDraft]);

  /**
   * Update an existing market condition category
   */
  const updateMarketConditionCategory = useCallback((
    tempId: string,
    updates: Partial<Omit<MarketConditionCategoryDraft, 'tempId'>>
  ): void => {
    const updatedCategories = marketConditionCategories.map(category =>
      category.tempId === tempId
        ? { ...category, ...updates }
        : category
    );

    setMarketConditionCategories(updatedCategories);
    syncToDraft(playbooks, criteriaItems, updatedCategories);
  }, [marketConditionCategories, criteriaItems, playbooks, syncToDraft]);

  /**
   * Remove a market condition category
   */
  const removeMarketConditionCategory = useCallback((tempId: string): void => {
    const updatedCategories = marketConditionCategories.filter(category => category.tempId !== tempId);
    setMarketConditionCategories(updatedCategories);
    syncToDraft(playbooks, criteriaItems, updatedCategories);
  }, [marketConditionCategories, criteriaItems, playbooks, syncToDraft]);

  /**
   * Get a specific market condition category by tempId
   */
  const getMarketConditionCategory = useCallback((tempId: string): MarketConditionCategoryDraft | undefined => {
    return marketConditionCategories.find(category => category.tempId === tempId);
  }, [marketConditionCategories]);

  /**
   * Add a new criteria item
   */
  const addCriteriaItem = useCallback((
    name: string,
    description?: string,
    categoryId?: string
  ): string => {
    const tempId = generateUUID();
    const newItem: CriteriaItemDraft = {
      tempId,
      name,
      ...(description && { description }),
      ...(categoryId && { categoryId })
    };

    const updatedItems = [...criteriaItems, newItem];
    setCriteriaItems(updatedItems);
    syncToDraft(playbooks, updatedItems, marketConditionCategories);
    return tempId;
  }, [criteriaItems, marketConditionCategories, playbooks, syncToDraft]);

  /**
   * Update an existing criteria item
   */
  const updateCriteriaItem = useCallback((
    tempId: string,
    updates: Partial<Omit<CriteriaItemDraft, 'tempId'>>
  ): void => {
    const updatedItems = criteriaItems.map(item =>
      item.tempId === tempId
        ? { ...item, ...updates }
        : item
    );

    setCriteriaItems(updatedItems);
    syncToDraft(playbooks, updatedItems, marketConditionCategories);
  }, [criteriaItems, marketConditionCategories, playbooks, syncToDraft]);

  /**
   * Remove a criteria item
   */
  const removeCriteriaItem = useCallback((tempId: string): void => {
    const updatedItems = criteriaItems.filter(item => item.tempId !== tempId);

    // Also remove this criteria from all playbooks that reference it
    const updatedPlaybooks = playbooks.map(playbook => ({
      ...playbook,
      criteriaIds: playbook.criteriaIds?.filter(id => id !== tempId) || []
    }));

    setCriteriaItems(updatedItems);
    setPlaybooks(updatedPlaybooks);
    syncToDraft(updatedPlaybooks, updatedItems, marketConditionCategories);
  }, [criteriaItems, marketConditionCategories, playbooks, syncToDraft]);

  /**
   * Get a specific criteria item by tempId
   */
  const getCriteriaItem = useCallback((tempId: string): CriteriaItemDraft | undefined => {
    return criteriaItems.find(item => item.tempId === tempId);
  }, [criteriaItems]);

  /**
   * Add a new confluence
   */
  const addConfluence = useCallback((
    name: string,
    description?: string,
    categoryId?: string
  ): string => {
    const tempId = generateUUID();
    const newConfluence: ConfluenceDraft = {
      tempId,
      name,
      ...(description && { description }),
      ...(categoryId && { categoryId })
    };

    const updatedConfluences = [...confluences, newConfluence];
    setConfluences(updatedConfluences);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, updatedConfluences, entryModels);
    return tempId;
  }, [confluences, playbooks, criteriaItems, marketConditionCategories, entryModels, syncToDraft]);

  /**
   * Update an existing confluence
   */
  const updateConfluence = useCallback((
    tempId: string,
    updates: Partial<Omit<ConfluenceDraft, 'tempId'>>
  ): void => {
    const updatedConfluences = confluences.map(confluence =>
      confluence.tempId === tempId
        ? { ...confluence, ...updates }
        : confluence
    );

    setConfluences(updatedConfluences);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, updatedConfluences, entryModels);
  }, [confluences, playbooks, criteriaItems, marketConditionCategories, entryModels, syncToDraft]);

  /**
   * Remove a confluence
   */
  const removeConfluence = useCallback((tempId: string): void => {
    const updatedConfluences = confluences.filter(confluence => confluence.tempId !== tempId);

    // Also remove this confluence from all playbooks that reference it
    const updatedPlaybooks = playbooks.map(playbook => ({
      ...playbook,
      confluences: playbook.confluences?.filter(link => link.confluenceId !== tempId) || []
    }));

    setConfluences(updatedConfluences);
    setPlaybooks(updatedPlaybooks);
    syncToDraft(updatedPlaybooks, criteriaItems, marketConditionCategories, updatedConfluences, entryModels);
  }, [confluences, playbooks, criteriaItems, marketConditionCategories, entryModels, syncToDraft]);

  /**
   * Get a specific confluence by tempId
   */
  const getConfluence = useCallback((tempId: string): ConfluenceDraft | undefined => {
    return confluences.find(confluence => confluence.tempId === tempId);
  }, [confluences]);

  /**
   * Add a new entry model
   */
  const addEntryModel = useCallback((
    name: string,
    description?: string,
    categoryId?: string
  ): string => {
    const tempId = generateUUID();
    const newModel: EntryModelDraft = {
      tempId,
      name,
      ...(description && { description }),
      ...(categoryId && { categoryId })
    };

    const updatedModels = [...entryModels, newModel];
    setEntryModels(updatedModels);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, confluences, updatedModels);
    return tempId;
  }, [entryModels, playbooks, criteriaItems, marketConditionCategories, confluences, syncToDraft]);

  /**
   * Update an existing entry model
   */
  const updateEntryModel = useCallback((
    tempId: string,
    updates: Partial<Omit<EntryModelDraft, 'tempId'>>
  ): void => {
    const updatedModels = entryModels.map(model =>
      model.tempId === tempId
        ? { ...model, ...updates }
        : model
    );

    setEntryModels(updatedModels);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, confluences, updatedModels);
  }, [entryModels, playbooks, criteriaItems, marketConditionCategories, confluences, syncToDraft]);

  /**
   * Remove an entry model
   */
  const removeEntryModel = useCallback((tempId: string): void => {
    const updatedModels = entryModels.filter(model => model.tempId !== tempId);

    // Also remove this entry model from all playbooks that reference it
    const updatedPlaybooks = playbooks.map(playbook => ({
      ...playbook,
      entryModelIds: playbook.entryModelIds?.filter(id => id !== tempId) || []
    }));

    setEntryModels(updatedModels);
    setPlaybooks(updatedPlaybooks);
    syncToDraft(updatedPlaybooks, criteriaItems, marketConditionCategories, confluences, updatedModels);
  }, [entryModels, playbooks, criteriaItems, marketConditionCategories, confluences, syncToDraft]);

  /**
   * Get a specific entry model by tempId
   */
  const getEntryModel = useCallback((tempId: string): EntryModelDraft | undefined => {
    return entryModels.find(model => model.tempId === tempId);
  }, [entryModels]);

  /**
   * Add a new entry model category
   */
  const addEntryModelCategory = useCallback((
    name: string,
    color: string,
    description?: string
  ): string => {
    const tempId = generateUUID();
    const newCategory: EntryModelCategoryDraft = {
      tempId,
      name,
      color,
      ...(description && { description }),
      order: entryModelCategories.length
    };

    const updatedCategories = [...entryModelCategories, newCategory];
    setEntryModelCategories(updatedCategories);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, updatedCategories);
    return tempId;
  }, [entryModelCategories, playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, syncToDraft]);

  /**
   * Update an existing entry model category
   */
  const updateEntryModelCategory = useCallback((
    tempId: string,
    updates: Partial<Omit<EntryModelCategoryDraft, 'tempId'>>
  ): void => {
    const updatedCategories = entryModelCategories.map(category =>
      category.tempId === tempId
        ? { ...category, ...updates }
        : category
    );

    setEntryModelCategories(updatedCategories);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, updatedCategories);
  }, [entryModelCategories, playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, syncToDraft]);

  /**
   * Remove an entry model category
   */
  const removeEntryModelCategory = useCallback((tempId: string): void => {
    const updatedCategories = entryModelCategories.filter(category => category.tempId !== tempId);
    setEntryModelCategories(updatedCategories);
    syncToDraft(playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, updatedCategories);
  }, [entryModelCategories, playbooks, criteriaItems, marketConditionCategories, confluences, entryModels, syncToDraft]);

  /**
   * Get a specific entry model category by tempId
   */
  const getEntryModelCategory = useCallback((tempId: string): EntryModelCategoryDraft | undefined => {
    return entryModelCategories.find(category => category.tempId === tempId);
  }, [entryModelCategories]);

  // Operations object
  const operations: PlaybookBuilderOperations = {
    addPlaybook,
    updatePlaybook,
    removePlaybook,
    getPlaybook,
    addMarketConditionCategory,
    updateMarketConditionCategory,
    removeMarketConditionCategory,
    getMarketConditionCategory,
    addCriteriaItem,
    updateCriteriaItem,
    removeCriteriaItem,
    getCriteriaItem,
    addConfluence,
    updateConfluence,
    removeConfluence,
    getConfluence,
    addEntryModel,
    updateEntryModel,
    removeEntryModel,
    getEntryModel,
    addEntryModelCategory,
    updateEntryModelCategory,
    removeEntryModelCategory,
    getEntryModelCategory
  };

  // State object
  const state: PlaybookBuilderState = {
    playbooks,
    criteriaItems,
    marketConditionCategories,
    confluences,
    entryModels,
    entryModelCategories
  };

  return [state, operations];
}
